import { useState, useContext, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { UserContext } from 'providers/userContextProvider';
import { AddModelPortfolioDetailsVisual } from './addModelPortfolioDetails.visual';
import { FETCH_THEMES } from '../../../../../pages/buildModels/index';
import { WorkflowContext } from '../../workflowCompletion';
import { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio';
import { Strategy } from '../../../../../interfaces/financialProduct';
import { useSaveModelPortfolio } from '../utils/useSaveModelPortfolio';

export interface AddModelPortfolioDetailsProps {
  options: {
    [key: string]: any;
  };
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: {
    [key: string]: any;
  };
}

export const AddModelPortfolioDetails = ({
  options,
  onNext,
  stepLoading,
  workflowCompletion,
}: AddModelPortfolioDetailsProps) => {
  const { activeOrganization } = useContext(UserContext);
  const { workflowData, setWorkflowData, updateWorkflowContext } = useContext(WorkflowContext);
  const [modelPortfolioData, setModelPortfolioData] = useState<ModelPortfolioData>({
    id: workflowData.modelPortfolioDetails?.id ?? '',
    strategy: workflowData.modelPortfolioDetails?.strategy ?? Strategy.STANDARD,
    themeId: workflowData.modelPortfolioDetails?.themeId ?? '',
    translatedName: workflowData.modelPortfolioDetails?.translatedName ?? { en: '', fr: '' },
    translatedPortfolioDescription: workflowData.modelPortfolioDetails?.translatedPortfolioDescription ?? { en: '', fr: '' },
    riskProfileName: workflowData.modelPortfolioDetails?.riskProfileName ?? { en: '', fr: '' },
    portfolioTemplate: workflowData.modelPortfolioDetails?.portfolioTemplate ?? null,
    securities: workflowData.modelPortfolioDetails?.securities ?? [],
    ...workflowData.modelPortfolioDetails,
    suggestable: workflowData.modelPortfolioDetails?.suggestable !== undefined
      ? workflowData.modelPortfolioDetails.suggestable
      : (options?.suggestable?.enabled ?? false),
    templateCreationInProgress: false,
  });
  const [originalValues, setOriginalValues] = useState<ModelPortfolioData>(workflowData.modelPortfolioDetails?.originalValues || modelPortfolioData);
  const { saveModelPortfolio, loading } = useSaveModelPortfolio();

  useEffect(() => {
    setWorkflowData({
      ...workflowData,
      modelPortfolioDetails: {
        ...modelPortfolioData,
        originalValues,
      },
    });
  }, [modelPortfolioData, originalValues, workflowData, setWorkflowData]);

  const { data: themesData } = useQuery(FETCH_THEMES, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id,
        },
        pagination: {
          perPage: 25,
          sortField: 'createdAt',
          sortDesc: false,
        },
      },
    },
    skip: !activeOrganization?.id,
  });

  const handleContinue = async (updatedModelPortfolioData: ModelPortfolioData) => {
    try {
      setModelPortfolioData(updatedModelPortfolioData);
      await setWorkflowData({
        ...workflowData,
        modelPortfolioDetails: {
          ...updatedModelPortfolioData,
          originalValues,
        },
      });

      if (updateWorkflowContext) {
        await updateWorkflowContext();
      }

      onNext();
    } catch (error) { /* empty */ }
  };

  const handleSaveForLater = async (updatedModelPortfolioData: ModelPortfolioData) => {
    try {
      const savedModelPortfolioData = await saveModelPortfolio(updatedModelPortfolioData);

      const savedData = {
        ...savedModelPortfolioData,
        originalValues: savedModelPortfolioData,
      };

      setModelPortfolioData(savedData);
      setOriginalValues(savedModelPortfolioData);
    } catch (error) {
      // Error is already handled by saveModelPortfolio
    }
  };

  return (
    <AddModelPortfolioDetailsVisual
      modelPortfolioData={modelPortfolioData}
      setModelPortfolioData={setModelPortfolioData}
      originalValues={originalValues}
      options={options}
      loading={stepLoading || loading}
      continueFunc={handleContinue}
      onSaveForLater={handleSaveForLater}
      themes={themesData?.fetchThemes?.themes ?? []}
      workflowCompletion={workflowCompletion}
    />
  );
};

export default AddModelPortfolioDetails;
